import { select, zoom, zoomIdentity, ZoomTransform, type D3ZoomEvent } from "d3";
import { readonly, ref, shallowRef, triggerRef, watch, type Ref } from "vue";
import type { SVGDimension } from "../interfaces";

type Props = {
	dimension: Ref<SVGDimension>;
	trackerRef: Ref<SVGGElement | undefined>;
};

const isMouseEvent = (e: any): e is MouseEvent => {
	return !!e && e.type;
};

export const useZoomBehavior = ({ dimension, trackerRef }: Props) => {
	const onZoom = (event: D3ZoomEvent<SVGGElement, unknown>) => {
		if (event.type !== "zoom") return;
		if (isMouseEvent(event.sourceEvent) && event.sourceEvent.type === "dblclick") return;

		transform.value = event.transform;
	};

	const zoomBehavior = zoom<SVGGElement, unknown>()
		.on("zoom", onZoom)
		// https://github.com/d3/d3-zoom#zoom_wheelDelta, but without the *10 when ctrl is pressed
		.wheelDelta((event: WheelEvent) => {
			return -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002);
		});

	const transform = ref<ZoomTransform>(zoomIdentity);

	const zoomPrevented = shallowRef();

	function isElementInScrollableContent(el: Element) {
		let parent: Element | null = el;

		while ((parent = parent.parentElement)) {
			if (parent instanceof HTMLElement && parent.scrollHeight > parent.offsetHeight) {
				return true;
			}
		}

		return false;
	}

	function isPageScrollable() {
		return trackerRef.value ? isElementInScrollableContent(trackerRef.value) : false;
	}

	const updateZoom = (tracker: SVGGElement, transform?: ZoomTransform) => {
		if (!transform) {
			transform = zoomIdentity;
		}

		select(tracker)
			.on(
				"touchmove",
				(e: TouchEvent) => {
					if (e.touches.length < 2 && isPageScrollable()) {
						e.stopImmediatePropagation();
						triggerRef(zoomPrevented);

						return;
					}

					e.preventDefault();
				},
				{ passive: false, capture: true }
			)
			.on(
				"wheel",
				(e: WheelEvent) => {
					if (!e.ctrlKey && !e.metaKey && isPageScrollable()) {
						e.stopImmediatePropagation();
						triggerRef(zoomPrevented);

						return;
					}

					e.preventDefault();
				},
				{ passive: false, capture: true }
			)
			.call(zoomBehavior.transform, transform);
	};

	watch(
		trackerRef,
		() => {
			if (!transform.value) return;
			if (!trackerRef.value) return;

			updateZoom(trackerRef.value, transform.value);
		},
		{ immediate: true }
	);

	function rescaleTransform(
		transform: ZoomTransform,
		newDimension: SVGDimension,
		previousDimension: SVGDimension
	) {
		if (previousDimension.width === 0 || previousDimension.height === 0) return;
		let { x, y } = transform;

		x = (x / previousDimension.width) * newDimension.width;
		y = (y / previousDimension.height) * newDimension.height;

		return new ZoomTransform(transform.k, x, y);
	}

	watch(dimension, async (dimension, previousDimension) => {
		if (
			dimension.height === previousDimension.height &&
			dimension.width === previousDimension.width
		) {
			return;
		}

		const tracker = trackerRef.value;
		if (!tracker) return;

		const { width, height } = dimension;
		zoomBehavior
			.scaleExtent([1, 1000])
			.translateExtent([
				[0, 0],
				[width, height],
			])
			.extent([
				[0, 0],
				[width, height],
			]);
		select(tracker).call(zoomBehavior).on("dblclick.zoom", null);
		updateZoom(tracker, rescaleTransform(transform.value, dimension, previousDimension));
	});

	function setTransform(transform: ZoomTransform) {
		if (!trackerRef.value) return;

		updateZoom(trackerRef.value, transform);
	}

	return { transform: readonly(transform), setTransform, zoomPrevented };
};
